import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, imageSrc } from "@/lib/api";

const spanFor = (i) => {
  const mod = i % 4;
  if (mod === 0) return "sm:col-span-2 sm:row-span-2";
  if (mod === 3) return "sm:col-span-2";
  return "";
};

export default function MenuSection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 sm:mb-20"
        >
          <p className="text-disco-teal text-xs font-semibold tracking-[0.35em] uppercase mb-4">
            The Menu
          </p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-8xl text-[#F8F8F8]">
            What's on <span className="neon-yellow">the grill</span>
          </h2>
          <p className="mt-5 max-w-lg text-sm sm:text-base text-zinc-400 font-light">
            Every dog is built to order on a toasted brioche bun. Photographed
            exactly how they land in your hands.
          </p>
        </motion.div>

        {loaded && items.length === 0 && (
          <p data-testid="menu-empty-state" className="text-zinc-500 text-sm">
            The menu is being restocked. Check back in a minute.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[240px] sm:auto-rows-[260px] gap-4 sm:gap-5">
          {items.map((item, i) => (
            <motion.article
              key={item.id}
              data-testid={`menu-item-${item.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden border border-white/10 bg-disco-surface ${spanFor(i)}`}
            >
              {item.image && (
                <img
                  src={imageSrc(item.image)}
                  alt={item.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

              {item.price && (
                <span
                  data-testid={`menu-price-${item.id}`}
                  className="absolute top-4 right-4 rounded-full bg-disco-yellow text-disco-void text-sm font-bold px-3.5 py-1"
                >
                  {item.price}
                </span>
              )}

              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                {item.tag && (
                  <span className="inline-block text-[10px] font-semibold tracking-[0.25em] uppercase text-disco-lavender mb-2">
                    {item.tag}
                  </span>
                )}
                <h3 className="font-display uppercase tracking-wide text-2xl sm:text-3xl text-[#F8F8F8] leading-none">
                  {item.name}
                </h3>
                {item.description && (
                  <p className="mt-2 text-xs sm:text-sm text-zinc-300/90 font-light max-w-sm line-clamp-2">
                    {item.description}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
